import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ImageCroppedEvent } from 'ngx-image-cropper';
import { WebcamImage } from 'ngx-webcam';
import { Subject } from 'rxjs';

import { BaseFormAbstractComponent } from '@misc/abstracts/base-form.abstract.component';
import { blobToDataUrl } from '@misc/helpers/blob-to-dataurl';
import { AppIcon } from '@models/enums/app.icons';
import { FileType } from '@models/enums/file-type.enum';
import { IAvatar } from '@models/interfaces/avatar.interface';
import { IModalComponentContext, ModalComponent } from '@shared/modal/modal.component';

@Component({
  selector: 'app-avatar-edit-modal',
  templateUrl: './avatar-edit-modal.component.html',
  styleUrls: ['./avatar-edit-modal.component.scss']
})
export class AvatarEditModalComponent extends BaseFormAbstractComponent implements OnInit {
  @Input() context: IModalComponentContext<IAvatar>;
  @ViewChild('inputPhoto') inputPhoto: ElementRef<HTMLInputElement>;
  
  // Image handling properties
  imageSource: string = '';
  croppedImage: string = '';
  isTakePhoto: boolean = false;
  
  // Other properties
  control: FormControl<string>;
  readonly AppIcon = AppIcon;
  takePhotoWeb: any = new Subject();

  constructor(
    private _translate: TranslateService,
    private _fb: FormBuilder
  ) {
    super();
  }

  get avatar(): IAvatar {
    return this.context?.entity;
  }

  get defaultValues(): IAvatar {
    return {
      src: this.avatar?.src || '',
      types: this.avatar?.types || [FileType.png, FileType.jpeg, FileType.jpg]
    } as IAvatar;
  }

  get dialog(): MatDialogRef<ModalComponent<IAvatar>> {
    return this.context?.dialog;
  }

  getFiles(event: Event): File[] {
    return Array.from((event.target as HTMLInputElement).files ?? []).map((file: File): File => file);
  }

  async fileChangeEvent(event: Event): Promise<any> {
    const img = this.getFiles(event)?.[0];
    if (!img) return;
    
    try {
      const dataUrl = await blobToDataUrl(img);
      
      // Set the image source for the cropper
      this.imageSource = dataUrl;
      
      // Set the control value for conditional rendering
      this.control.setValue(dataUrl);
      
      // Reset cropped image
      this.croppedImage = '';
    } catch (error) {
      console.error('Error loading image:', error);
    }
  }

  imageCropped(event: ImageCroppedEvent): void {
    // Handle the case where we have a blob or objectUrl instead of base64
    if (event.blob && event.objectUrl) {
      this.croppedImage = event.objectUrl;
      return;
    }
    
    // Fallback to base64 if available
    if (event.base64) {
      this.croppedImage = event.base64;
    }
  }

  ngOnInit(): void {
    // Initialize the form
    this._initForm();
    
    // Initialize the image source and control with proper validation
    if (this.defaultValues.src) {
      // Ensure the image source is properly formatted for the cropper
      this.validateAndSetImageSource(this.defaultValues.src);
    } else {
      this.imageSource = '';
      this.control = new FormControl<string>('');
    }
  }

  // Validate and set image source to ensure it's in the correct format
  private validateAndSetImageSource(source: string): void {
    if (!source) {
      this.imageSource = '';
      this.control = new FormControl<string>('');
      return;
    }

    // If it's already a data URL, use it directly
    if (source.startsWith('data:image/')) {
      this.imageSource = source;
      this.control = new FormControl<string>(source);
      return;
    }

    // For other formats (like URLs), create a placeholder and load the image properly
    this.imageSource = '';
    this.control = new FormControl<string>(source);
    
    // Load the image properly if it's a URL
    if (source.startsWith('http')) {
      this.loadImageFromUrl(source);
    }
  }

  // Load image from URL and convert to base64
  private async loadImageFromUrl(url: string): Promise<void> {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64data = reader.result as string;
        this.imageSource = base64data;
        this.control.setValue(base64data);
      };
      reader.readAsDataURL(blob);
    } catch (error) {
      console.error('Error loading image from URL:', error);
    }
  }

  async getModalResult(): Promise<IAvatar> {
    let src = this.croppedImage || this.imageSource;
    
    if (src && src.startsWith('blob:')) {
      src = await this._blobUrlToBase64(src);
    }
    
    return {
      src: src,
      types: this.defaultValues.types
    };
  }

  onSubmit(): void {
    this.getModalResult().then(result => {
      this.dialog.close(result);
    });
  }

  takePhoto(): void {
    if (!this.isTakePhoto) {
      this.isTakePhoto = true;
      this.dialog.componentInstance.data = {
        ...this.dialog.componentInstance.data,
        title: 'MODALS.PHOTO_HEADER',
        actions: null
      };
    } else {
      this.dialog.componentInstance.data = {
        ...this.dialog.componentInstance.data,
        title: 'MODALS.AVATAR_HEADER',
        actions: [
          { name: 'BUTTON_NAME.CANCEL', type: 'close', color: 'accent' },
          { name: 'BUTTON_NAME.SAVE', type: 'submit', color: 'primary' }
        ]
      };
      this.takePhotoWeb.next();
      this.isTakePhoto = false;
    }
  }

  saveImage(event: WebcamImage): void {
    if (!event.imageAsDataUrl) return;
    
    this.imageSource = event.imageAsDataUrl;
    this.control.setValue(event.imageAsDataUrl);
    this.croppedImage = '';
  }

  focusToSubmit(): void {
    (document.querySelector('.modal-actions__button-submit') as HTMLButtonElement)?.focus?.();
  }

  private _initForm(): void {
    this.formGroup = this._fb.group({
      src: this._fb.control(this.defaultValues.src)
    });
  }

  // Helper method to convert blob URL to base64
  private async _blobUrlToBase64(blobUrl: string): Promise<string> {
    try {
      // Fetch the blob from the URL
      const response = await fetch(blobUrl);
      const blob = await response.blob();
      
      // Convert blob to base64
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error converting blob URL to base64:', error);
      return '';
    }
  }
}
